import { autoinject } from 'aurelia-framework';
import { ValidationController, Validator } from 'aurelia-validation';
import { I18N } from 'aurelia-i18n';
import { AquacomComponentType, MooringArticleCategory, MooringArticleType, MooringArticleTypeVariant } from 'models/mooring';
import { MooringArticleCategoryService, MooringArticleTypeService, ToastService } from 'services';
import { StandardValidationBuilder } from 'lib/validation';
import { DialogController } from 'aurelia-dialog';
import { AsyncErrorHandler } from 'lib/ui';

@autoinject
export class MooringArticleTypeCreateEdit {
  ready = false;

  protected categories: MooringArticleCategory[] = [];
  protected aquacomTypes: (AquacomComponentType & { PathName: string })[] = [];

  protected form: MooringArticleType = {
    Id: undefined,
    Name: undefined,
    NameEn: undefined,
    NameEs: undefined,
    CategoryId: undefined,
    VariantId: undefined,
  };

  private variants: MooringArticleTypeVariant[] = [];

  private categoryLocked: boolean = false;

  constructor(
    protected validator: Validator,
    private validationController: ValidationController,
    private mooringCategoryService: MooringArticleCategoryService,
    private mooringArticleTypeService: MooringArticleTypeService,
    private t: I18N,
    private toast: ToastService,
    private dialog: DialogController,
    private validationBuilder: StandardValidationBuilder
  ) {}

  protected applyValidationRules() {
    this.validationBuilder.with(this.form).required('Name').required('NameEn').required('CategoryId').done();
  }

  protected activate(params?: { typeId?: number; categoryId?: number; aquacomCategoryId: number }) {
    void this.setup(params?.typeId, params?.categoryId, params?.aquacomCategoryId);
  }

  protected async setup(typeId: number, categoryId: number, aquacomCategoryId: number) {
    await this.getCategories();
    await this.getAquacomCategories(aquacomCategoryId);
    await this.getVariants();

    if (typeId) {
      await this.getCategoryType(typeId);
    } else {
      this.form.CategoryId = categoryId;
      this.categoryLocked = true;
    }

    this.applyValidationRules();
    this.ready = true;
  }

  @AsyncErrorHandler
  protected async getCategoryType(typeId: number) {
    const theType = await this.mooringArticleTypeService.get(typeId);
    this.form = {
      ...theType,
    };
  }

  @AsyncErrorHandler
  protected async getAquacomCategories(aquacomCategoryId: number) {
    const types = await this.mooringArticleTypeService.getAquacomTypes(aquacomCategoryId);
    this.aquacomTypes = types.map((t) => ({
      ...t,
      PathName: `${t.CategoryName} - ${t.Name}`,
    }));
  }

  @AsyncErrorHandler
  async getCategories() {
    this.categories = await this.mooringCategoryService.getAll();
  }

  @AsyncErrorHandler
  protected async onSave(articleType: MooringArticleType) {
    if (articleType.Id) {
      delete articleType.Category;

      await this.mooringArticleTypeService.put(articleType, articleType.Id);
    } else {
      await this.mooringArticleTypeService.post(articleType);
    }
    this.toast.showSuccess(this.t.tr('general.saved'));
    this.dialog.close(true);
  }

  @AsyncErrorHandler
  protected async getVariants() {
    this.variants = await this.mooringArticleTypeService.getVariants();
  }

  @AsyncErrorHandler
  async save() {
    const { valid } = await this.validationController.validate();
    if (!valid) return;
    this.onSave?.(this.form);
  }

  @AsyncErrorHandler
  protected async onDelete() {
    if (!this.form.Id) return;

    await this.mooringArticleTypeService.delete(this.form.Id);
    this.toast.showSuccess(this.t.tr('general.deleted'));
    this.cancel();
  }

  cancel() {
    this.dialog.close(false);
  }
}
